import {
  Component,
  EventEmitter,
  HostListener,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { MailAccountClient } from '@app-services/api/clients/mail-account.client';
import { TagClient } from '@app-services/api/clients/tag.client';
import { MailAccountContract } from '@app-types/api/mail-account';
import { SomethingWentWrongComponent } from '../common/error/something-went-wrong/something-went-wrong.component';
import { MatButtonModule } from '@angular/material/button';
import { MAT_SELECT_CONFIG, MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  EMAIL_QUERY_PARAM,
  STORAGE_NAMES,
} from '@app-shared/constants/constants';
import { LocalStorageService } from '@app-services/local-storage.service';
import { decodeInfoMailAccount } from '@app-utils/search-params';
import { transformEnum } from '@app-utils/common';
import {
  AttachmentsEnum,
  NotificationsEnum,
  ReassignedEnum,
  SearchTypeEnum,
  SeenEnum,
  SourceEnum,
  StarredEnum,
  ValidatedEnum,
} from '@app-types/enums/filters';
import { EmailSearchTagInputComponent } from './email-search-tag-input/email-search-tag-input.component';
import { SearchParams } from '@app-types/search';
import { SkeletonFormDrawerComponent } from '@app-components/common/skeletons/skeleton-form-drawer/skeleton-form-drawer.component';
import { TagContract } from '@app-types/api/tag';
import { TagType } from '@app-types/enums/tags';
import { sortByTextField } from 'src/app/shared/utils/sortData';
import {
  SearchableSelectComponent,
  SearchableSelectOption,
} from '../common/searchable-select/searchable-select.component';

const { SEARCH_TYPE } = STORAGE_NAMES;

@Component({
  selector: 'app-email-search-advanced-dialog',
  imports: [
    SomethingWentWrongComponent,
    MatSelectModule,
    MatInputModule,
    MatDatepickerModule,
    MatButtonModule,
    MatFormFieldModule,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    SkeletonFormDrawerComponent,
    EmailSearchTagInputComponent,
    SearchableSelectComponent,
  ],
  templateUrl: './email-search-advanced-dialog.component.html',
  styleUrls: ['./email-search-advanced-dialog.component.scss'],
  providers: [
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: '' },
    },
  ],
})
export class EmailSearchAdvancedDialogComponent implements OnInit {
  @Output() closeEventEmitter = new EventEmitter();
  @Output() searchFilters = new EventEmitter();
  public selectedMailAccountId: number;
  public isLoading = true;
  public cannotLoadData = false;
  public filters: SearchParams = new SearchParams();

  public selectedFullTags = new Set<TagContract>();
  public initialTagOptions: TagContract[] | null = null;
  readonly tagType = TagType.Email;
  public seen = transformEnum(SeenEnum);
  public attachments = transformEnum(AttachmentsEnum);
  public source = transformEnum(SourceEnum);
  public starred = transformEnum(StarredEnum);
  public notifications = transformEnum(NotificationsEnum);
  public reassigned = transformEnum(ReassignedEnum);
  public validated = transformEnum(ValidatedEnum);
  public searchType = transformEnum(SearchTypeEnum);

  public selectedAttachmentsStatus: AttachmentsEnum;
  public selectedSourceStatus: SourceEnum;
  public selectedStarred: StarredEnum;
  public selectedHideNotificationsStatus: NotificationsEnum;
  public selectedSeenStatus: SeenEnum;
  public selectedReassignedId: number = ReassignedEnum.all;
  public selectedReassignedStatus: ReassignedEnum;
  // TODO
  public selectedValidationStatus: any;
  public accounts: MailAccountContract[] = [];
  public reassignedAccountOptions: SearchableSelectOption<number>[] = [];
  public selectedSearchType: number = 1;

  public filterForm: UntypedFormGroup = new UntypedFormGroup({
    sentStart: new UntypedFormControl(),
    sentEnd: new UntypedFormControl(),
    downloadStart: new UntypedFormControl(),
    downloadEnd: new UntypedFormControl(),
  });

  get hasMailAccount(): boolean {
    return !!this.selectedMailAccountId;
  }

  constructor(
    public matchError: MatchError,
    private mailAccountClient: MailAccountClient,
    private tagClient: TagClient,
    private localStorageService: LocalStorageService,
    private translate: TranslateService
  ) {}

  async ngOnInit(): Promise<void> {
    if (window.location.search) {
      const newUrl = new URL(window.location.href);
      if (newUrl.pathname.includes('emails')) {
        const search: string = newUrl.searchParams.get(EMAIL_QUERY_PARAM) ?? '';
        const data = decodeInfoMailAccount(search);
        data?.acc && (this.selectedMailAccountId = data.acc as number);
        data?.filter && (this.filters = data.filter as SearchParams);
      }
    }
    if (this.filters.reassignedId === null) {
      this.selectedReassignedId = 0;
    } else {
      this.selectedReassignedId = this.filters.reassignedId;
    }
    this.selectedSearchType =
      this.localStorageService.getData(SEARCH_TYPE) || 1;
    if (this.filters.reassigned === null) {
      this.selectedReassignedStatus = ReassignedEnum.all;
    } else if (!this.filters.reassigned) {
      this.selectedReassignedStatus = ReassignedEnum['not reassigned'];
    } else {
      this.selectedReassignedStatus = ReassignedEnum.reassigned;
    }
    if (this.filters.seen === null) {
      this.selectedSeenStatus = SeenEnum.showAll;
    } else if (!this.filters.seen) {
      this.selectedSeenStatus = SeenEnum.onlyUnseen;
    } else {
      this.selectedSeenStatus = SeenEnum.onlySeen;
    }

    if (this.filters.isStarred === null) {
      this.selectedStarred = StarredEnum.showAll;
    } else if (!this.filters.isStarred) {
      this.selectedStarred = StarredEnum.no;
    } else {
      this.selectedStarred = StarredEnum.yes;
    }

    if (this.filters.withAttachments === null) {
      this.selectedAttachmentsStatus = AttachmentsEnum.showAll;
    } else if (!this.filters.withAttachments) {
      this.selectedAttachmentsStatus = AttachmentsEnum.onlyWithoutAttachments;
    } else {
      this.selectedAttachmentsStatus = AttachmentsEnum.onlyWithAttachments;
    }
    if (this.filters.source === null) {
      this.selectedSourceStatus = SourceEnum.showAll;
    } else if (!this.filters.source) {
      this.selectedSourceStatus = SourceEnum.mailboxPEC;
    } else {
      this.selectedSourceStatus = SourceEnum.reassignment;
    }
    if (!this.filters.hideNotifications) {
      this.selectedHideNotificationsStatus = NotificationsEnum.showAll;
    } else {
      this.selectedHideNotificationsStatus =
        NotificationsEnum['Only without notifications'];
    }

    this.filterForm.setValue({
      sentStart: this.filters.sentDateTime.start || null,
      sentEnd: this.filters.sentDateTime.end
        ? this.substractDays(this.filters.sentDateTime.end, 1)
        : null,
      downloadStart: this.filters.downloadDateTime.start || null,
      downloadEnd: this.filters.downloadDateTime.end || null,
    });

    if (this.hasMailAccount) {
      await this.loadData();
    } else {
      this.buildReassignedAccountOptions();
      this.isLoading = false;
      this.selectedFullTags = new Set();
      this.initialTagOptions = null;
    }
  }

  private createTagStub(tag: string): TagContract {
    return {
      tagId: 0,
      tag,
      mailAccountId: this.selectedMailAccountId,
      type: TagType.Email,
    };
  }

  private async resolveSelectedTags(loadedTags: TagContract[]): Promise<void> {
    const tagNames = this.filters.tags ?? [];
    if (!tagNames.length) {
      this.selectedFullTags = new Set();
      return;
    }

    const resolved: TagContract[] = [];
    const missing: string[] = [];

    for (const name of tagNames) {
      const found = loadedTags.find(
        tag => tag.tag.toLowerCase() === name.toLowerCase()
      );
      if (found) {
        resolved.push(found);
      } else {
        missing.push(name);
      }
    }

    if (missing.length) {
      const extra = await Promise.all(
        missing.map(async name => {
          try {
            const response = await this.tagClient.searchTagsForSelect(
              this.selectedMailAccountId,
              TagType.Email,
              name
            );
            return (
              response.data.find(
                tag => tag.tag.toLowerCase() === name.toLowerCase()
              ) ?? this.createTagStub(name)
            );
          } catch (e) {
            this.matchError.logError(e);
            return this.createTagStub(name);
          }
        })
      );
      resolved.push(...extra);
    }

    this.selectedFullTags = new Set(resolved);
  }

  onSearchTypeChange(value: number): void {
    this.localStorageService.setData(SEARCH_TYPE, value);
  }

  addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  substractDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() - days);
    return result;
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.search();
    }
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    this.cannotLoadData = false;
    try {
      const [mailAccountRequest, tagsResponse] = await Promise.all([
        this.mailAccountClient.getOrganizationMailAccounts(),
        this.tagClient.searchTagsForSelect(
          this.selectedMailAccountId,
          TagType.Email,
          null
        ),
      ]);
      this.accounts = mailAccountRequest.data;
      this.buildReassignedAccountOptions();
      this.initialTagOptions = tagsResponse.data;
      await this.resolveSelectedTags(tagsResponse.data);
    } catch (e) {
      this.cannotLoadData = true;
      this.matchError.logError(e);
    } finally {
      this.isLoading = false;
    }
  }

  async search(): Promise<void> {
    this.selectedReassignedId === ReassignedEnum.all
      ? (this.filters.reassignedId = null)
      : (this.filters.reassignedId = this.selectedReassignedId);
    this.filters.sentDateTime.start = this.filterForm.controls.sentStart?.value;
    this.filters.sentDateTime.end =
      this.filterForm.controls.sentEnd?.value &&
      this.addDays(this.filterForm.controls.sentEnd?.value, 1);
    this.filters.downloadDateTime.start =
      this.filterForm.controls.downloadStart?.value;
    this.filters.downloadDateTime.end =
      this.filterForm.controls.downloadEnd?.value;

    this.filters.validated =
      this.selectedValidationStatus &&
      this.selectedValidationStatus !== ValidatedEnum.all
        ? [this.selectedValidationStatus]
        : null;

    this.filters.source =
      this.selectedSourceStatus === SourceEnum.showAll
        ? null
        : this.selectedSourceStatus !== SourceEnum.mailboxPEC;

    this.filters.reassigned =
      this.selectedReassignedStatus === ReassignedEnum.all
        ? null
        : this.selectedReassignedStatus !== ReassignedEnum['not reassigned'];

    this.filters.seen =
      this.selectedSeenStatus === SeenEnum.showAll
        ? null
        : this.selectedSeenStatus !== SeenEnum.onlyUnseen;

    this.filters.isStarred =
      this.selectedStarred === StarredEnum.showAll
        ? null
        : this.selectedStarred === StarredEnum.yes;

    this.filters.withAttachments =
      this.selectedAttachmentsStatus === AttachmentsEnum.showAll
        ? null
        : this.selectedAttachmentsStatus !==
          AttachmentsEnum.onlyWithoutAttachments;

    this.filters.hideNotifications =
      this.selectedHideNotificationsStatus !== NotificationsEnum.showAll;

    this.filters.tags = this.hasMailAccount
      ? Array.from(this.selectedFullTags).map(el => el.tag)
      : [];

    this.searchFilters.emit({
      filter: this.filters,
      searchType: this.selectedSearchType,
    });
  }

  resetParams(): void {
    this.selectedSearchType = 1;
    this.selectedReassignedId = ReassignedEnum.all;
    this.selectedAttachmentsStatus = AttachmentsEnum.showAll;
    this.selectedSeenStatus = SeenEnum.showAll;
    this.selectedSourceStatus = SourceEnum.showAll;
    this.selectedFullTags = new Set<TagContract>();
    this.filterForm.setValue({
      sentStart: null,
      sentEnd: null,
      downloadStart: null,
      downloadEnd: null,
    });
    this.filters = new SearchParams();
    this.buildReassignedAccountOptions();
    this.searchFilters.emit();
  }

  private buildReassignedAccountOptions(): void {
    this.reassignedAccountOptions = [
      { value: 0, label: this.translate.instant('showAll') },
      ...sortByTextField(
        this.accounts.map(account => ({
          value: account.mailAccountId,
          label: account.name,
        })),
        'label'
      ),
    ];
  }
}
